import { useState, useRef, useEffect } from 'react';
import {
  Plus,
  Search,
  Calendar,
  SlidersHorizontal,
  ArrowUp,
  ChevronDown,
  ChevronRight,
  MoreHorizontal,
  Play,
  CheckCircle2,
  Circle,
  Loader2,
  FileText,
  Code2,
  Settings2,
  Mail,
  Database,
  Copy,
  ThumbsUp,
  ThumbsDown,
  RotateCcw,
  Zap,
} from 'lucide-react';
import { cn } from './ui/utils';

/* ───────── Types ───────── */

interface CoworkTask {
  id: string;
  title: string;
  preview: string;
  timestamp: string;
  status: 'running' | 'completed' | 'idle';
}

interface ProgressStep {
  id: string;
  label: string;
  status: 'done' | 'running' | 'pending';
  detail?: string;
}

interface AgentFile {
  name: string;
  icon: 'file' | 'folder' | 'code' | 'config' | 'mail';
}

interface Connector {
  name: string;
  icon: string;
  color: string;
}

interface CoworkMessage {
  id: string;
  role: 'user' | 'agent';
  content: string;
  timestamp: string;
  blocks?: ActionBlock[];
}

interface ActionBlock {
  type: 'api-call' | 'status-change' | 'code' | 'result';
  label?: string;
  method?: string;
  endpoint?: string;
  statusFrom?: string;
  statusTo?: string;
  code?: string;
  resultText?: string;
  resultStatus?: 'success' | 'error' | 'info';
}

/* ───────── Mock data ───────── */

const recentTasks: CoworkTask[] = [
  { id: 't1', title: 'Build influencer marketing automation agent', preview: 'Campaign lifecycle: setup → launch → payout', timestamp: '2m ago', status: 'running' },
  { id: 't2', title: 'Compile Swedish influencers list', preview: 'Found 847 creators matching criteria', timestamp: '1h ago', status: 'completed' },
  { id: 't3', title: 'Launch SaaS businesses to generate leads', preview: 'Configured outreach pipeline for 3 brands', timestamp: '3h ago', status: 'completed' },
  { id: 't4', title: 'Create 20 creator lists for Beatly app', preview: 'Lists organized by niche, size, platform', timestamp: 'Yesterday', status: 'completed' },
  { id: 't5', title: 'Find recent Merrell mentions on Instagram', preview: 'Tracked 156 brand mentions in last 7 days', timestamp: 'Yesterday', status: 'completed' },
  { id: 't6', title: 'Compare Merrell and Salomon brand lift', preview: 'Brand awareness delta: +8.2% vs +5.1%', timestamp: '2 days ago', status: 'completed' },
  { id: 't7', title: 'Build app playlisting system with criteria', preview: 'Playlist engine with 12 filter dimensions', timestamp: '3 days ago', status: 'completed' },
  { id: 't8', title: 'Develop app for day two', preview: 'Sprint planning + component scaffolding', timestamp: '4 days ago', status: 'completed' },
];

const defaultProgressSteps: ProgressStep[] = [
  { id: 's1', label: 'Campaign setup & brief creation', status: 'done', detail: 'Created 3 briefs with content guidelines' },
  { id: 's2', label: 'Creator curation & invites', status: 'done', detail: 'Sent 24 invites, 18 accepted' },
  { id: 's3', label: 'Content review & approval', status: 'done', detail: 'All 18 drafts approved' },
  { id: 's4', label: 'Publication & tracking', status: 'running', detail: 'Monitoring live posts...' },
  { id: 's5', label: 'Payout processing', status: 'pending' },
];

const agentFiles: AgentFile[] = [
  { name: 'instructions - CLAUDE.md', icon: 'file' },
  { name: 'beatly-agent-plan.html', icon: 'file' },
  { name: 'package.json', icon: 'config' },
  { name: 'tsconfig.json', icon: 'config' },
  { name: 'beatly.ts', icon: 'code' },
  { name: 'emails.ts', icon: 'mail' },
  { name: 'definitions.ts', icon: 'code' },
  { name: 'prompts.ts', icon: 'code' },
  { name: 'agent.ts', icon: 'code' },
  { name: 'index.ts', icon: 'code' },
];

const connectors: Connector[] = [
  { name: 'Beatly Platform', icon: '⚡', color: '#be43ff' },
  { name: 'Instagram API', icon: '📸', color: '#E1306C' },
  { name: 'Gmail Integration', icon: '✉️', color: '#EA4335' },
  { name: 'Slack', icon: '💬', color: '#4A154B' },
];

const defaultMessages: CoworkMessage[] = [
  {
    id: 'm1',
    role: 'agent',
    content: 'The **Nike Planner 2026** campaign is now DONE! Here\'s a summary of everything that was completed in this session:',
    timestamp: '2m ago',
    blocks: [
      {
        type: 'status-change',
        label: 'Henri Brana\'s acceptance',
        statusFrom: 'PENDING_REVIEW',
        statusTo: 'ACCEPT_NEW_BUDGET',
        resultText: 'The webhook email reply (33333 SEK, accept) was classified and the invite was accepted via API.',
        resultStatus: 'success',
      },
      {
        type: 'api-call',
        label: 'Publications created',
        method: 'POST',
        endpoint: '/bo/v2/campaign/publications/create',
        resultText: 'Two Instagram Post publications were created and marked as published with permalink and metrics like likes, impressions, reach, comments, saves, shares.',
        resultStatus: 'success',
      },
      {
        type: 'status-change',
        label: 'Payout flow completed',
        statusFrom: 'NOT_QUALIFIED',
        statusTo: 'PAID',
        resultText: 'The collaboration was moved through the full payout state machine: NOT_QUALIFIED → PENDING_REVIEW → PENDING_REQUEST → REQUESTED → PAID',
        resultStatus: 'success',
      },
      {
        type: 'api-call',
        label: 'Campaign marked DONE',
        method: 'PATCH',
        endpoint: '/bo/v2/campaign/5cQnLHZf-cpg/mark-as-done',
        resultText: 'status: DONE ✅',
        resultStatus: 'success',
      },
    ],
  },
  {
    id: 'm2',
    role: 'agent',
    content: 'The full end-to-end campaign lifecycle — from beat setup, launch, curation, invites, email negotiation, acceptance, content delivery, and payout — is complete.',
    timestamp: '2m ago',
  },
];

const emptyStateMessages: CoworkMessage[] = [];

/* ───────── Helpers ───────── */

function getFileIcon(type: AgentFile['icon']) {
  switch (type) {
    case 'code': return <Code2 className="w-3.5 h-3.5 text-[#be43ff]" />;
    case 'config': return <Settings2 className="w-3.5 h-3.5 text-[#667085]" />;
    case 'mail': return <Mail className="w-3.5 h-3.5 text-[#00b1a5]" />;
    default: return <FileText className="w-3.5 h-3.5 text-[#98a2b3]" />;
  }
}

/* ───────── Component ───────── */

export function CoworkPage() {
  const [selectedTask, setSelectedTask] = useState<string | null>('t1');
  const [searchQuery, _setSearchQuery] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [messages, setMessages] = useState<CoworkMessage[]>(defaultMessages);
  const [rightPanelOpen, setRightPanelOpen] = useState(true);
  const [progressSteps, setProgressSteps] = useState(defaultProgressSteps);
  const [contextExpanded, setContextExpanded] = useState(true);
  const [filesExpanded, setFilesExpanded] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const filteredTasks = searchQuery
    ? recentTasks.filter(t => t.title.toLowerCase().includes(searchQuery.toLowerCase()))
    : recentTasks;

  const activeTask = recentTasks.find(t => t.id === selectedTask);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = () => {
    if (!inputValue.trim()) return;
    const userMsg: CoworkMessage = {
      id: `msg-${Date.now()}`,
      role: 'user',
      content: inputValue.trim(),
      timestamp: 'Just now',
    };
    setMessages(prev => [...prev, userMsg]);
    setInputValue('');

    // Simulate agent performing actions
    setTimeout(() => {
      const agentMsg = generateAgentResponse(userMsg.content);
      setMessages(prev => [...prev, agentMsg]);
    }, 1200);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNewTask = () => {
    setSelectedTask(null);
    setMessages(emptyStateMessages);
    setInputValue('');
    setProgressSteps([]);
  };

  const handleSelectTask = (id: string) => {
    setSelectedTask(id);
    if (id === 't1') {
      setMessages(defaultMessages);
      setProgressSteps(defaultProgressSteps);
    } else {
      const task = recentTasks.find(t => t.id === id);
      setMessages([
        {
          id: 'hist-1',
          role: 'agent',
          content: `Task "${task?.title}" has been completed. Here's what was accomplished:`,
          timestamp: task?.timestamp || '',
          blocks: [
            {
              type: 'result',
              resultText: task?.preview || '',
              resultStatus: 'success',
            },
          ],
        },
      ]);
      setProgressSteps([
        { id: 'cs1', label: 'Task initialized', status: 'done' },
        { id: 'cs2', label: 'Processing', status: 'done' },
        { id: 'cs3', label: 'Complete', status: 'done' },
      ]);
    }
  };

  return (
    <div className="flex h-[calc(100vh-3.5rem)] bg-[#fafafa]">
      {/* ────────────── Left Sidebar ────────────── */}
      <div className="w-[260px] shrink-0 bg-white border-r-0 flex flex-col">
        {/* New task button */}
        <div className="p-3 pb-2">
          <button
            onClick={handleNewTask}
            className="w-full flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-[13px] text-[#101828] hover:bg-[#f8f5ff] transition-colors border border-gray-100"
            style={{ fontWeight: 600 }}
          >
            <Plus className="w-4 h-4 text-[#be43ff]" />
            New task
          </button>
        </div>

        {/* Quick actions */}
        <div className="px-3 pb-2 flex flex-col gap-0.5">
          {[
            { icon: Search, label: 'Search' },
            { icon: Calendar, label: 'Scheduled' },
            { icon: SlidersHorizontal, label: 'Customize' },
          ].map((item) => (
            <button
              key={item.label}
              className="flex items-center gap-2.5 px-3 py-1.5 rounded-lg text-[12px] text-[#667085] hover:bg-[#f9fafb] hover:text-[#344054] transition-colors"
              style={{ fontWeight: 500 }}
            >
              <item.icon className="w-3.5 h-3.5" />
              {item.label}
            </button>
          ))}
        </div>

        {/* Divider */}
        <div className="mx-3 border-t border-gray-100 my-1" />

        {/* Recents label */}
        <div className="px-5 pt-2 pb-1">
          <span className="text-[11px] text-[#98a2b3] uppercase tracking-wider" style={{ fontWeight: 600 }}>Recents</span>
        </div>

        {/* Task list */}
        <div className="flex-1 overflow-y-auto px-2 pb-3">
          {filteredTasks.map((task) => (
            <button
              key={task.id}
              onClick={() => handleSelectTask(task.id)}
              className={cn(
                'w-full text-left px-3 py-2 rounded-xl transition-all duration-150 group mb-0.5 flex items-start gap-2',
                selectedTask === task.id
                  ? 'bg-[#f8f5ff] border border-[#be43ff]/15'
                  : 'hover:bg-[#f9fafb] border border-transparent'
              )}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  {task.status === 'running' && (
                    <span className="w-1.5 h-1.5 rounded-full bg-[#a9f600] shrink-0 animate-pulse" />
                  )}
                  <span
                    className={cn(
                      'text-[12px] truncate',
                      selectedTask === task.id ? 'text-[#be43ff]' : 'text-[#344054]'
                    )}
                    style={{ fontWeight: 500 }}
                  >
                    {task.title}
                  </span>
                </div>
              </div>
              <button
                className="opacity-0 group-hover:opacity-100 shrink-0 mt-0.5 text-[#98a2b3] hover:text-[#667085]"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreHorizontal className="w-3.5 h-3.5" />
              </button>
            </button>
          ))}
        </div>
      </div>

      {/* ────────────── Main Chat Area ────────────── */}
      <div className="flex-1 flex flex-col min-w-0 relative bg-[#fafafa] rounded-tl-[24px]">
        {/* Concave corner cutout */}
        <div className="absolute top-0 left-0 z-10 pointer-events-none" style={{ width: 24, height: 24 }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M24 0H0V24C0 10.745 10.745 0 24 0Z" fill="white" />
          </svg>
        </div>

        {/* Task header bar */}
        <div className="h-12 shrink-0 flex items-center justify-between px-5 border-b border-gray-100/60">
          <div className="flex items-center gap-2">
            <span className="text-[13px] text-[#344054]" style={{ fontWeight: 500 }}>
              {activeTask?.title || 'New task'}
            </span>
            <ChevronDown className="w-3.5 h-3.5 text-[#98a2b3]" />
          </div>
          <div className="flex items-center gap-1">
            {activeTask?.status === 'running' && (
              <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#a9f600]/15 text-[11px] text-[#5d7a00] mr-2" style={{ fontWeight: 600 }}>
                <span className="w-1.5 h-1.5 rounded-full bg-[#a9f600] animate-pulse" />
                Running
              </span>
            )}
            <button
              onClick={() => setRightPanelOpen(!rightPanelOpen)}
              className={cn(
                'w-8 h-8 flex items-center justify-center rounded-lg transition-colors',
                rightPanelOpen
                  ? 'text-[#be43ff] bg-[#be43ff]/8'
                  : 'text-[#98a2b3] hover:text-[#667085] hover:bg-gray-100'
              )}
              title="Toggle context panel"
            >
              <Database className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Content area: messages + optional right panel */}
        <div className="flex-1 flex overflow-hidden">
          {/* Messages column */}
          <div className="flex-1 flex flex-col min-w-0">
            {!selectedTask && messages.length === 0 ? (
              /* ─── Empty State ─── */
              <div className="flex-1 flex flex-col items-center justify-center px-6">
                <div className="max-w-[600px] w-full flex flex-col items-center">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#be43ff] to-[#8b1fd4] flex items-center justify-center mb-4 shadow-[0_4px_16px_rgba(190,67,255,0.25)]">
                    <Zap className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-[24px] text-[#101828] mb-2" style={{ fontWeight: 500 }}>
                    What should we work on?
                  </h2>
                  <p className="text-[14px] text-[#667085] mb-8 text-center max-w-[420px]">
                    I can automate campaigns, manage creators, process payouts, update statuses, and interact with the Beatly platform on your behalf.
                  </p>

                  {/* Suggestion cards */}
                  <div className="grid grid-cols-2 gap-3 w-full mb-8">
                    {[
                      { title: 'Launch a campaign', desc: 'Set up briefs, invite creators, and go live', icon: Play, color: '#be43ff' },
                      { title: 'Process all payouts', desc: 'Move qualified collabs through payout flow', icon: CheckCircle2, color: '#a9f600' },
                      { title: 'Compile creator list', desc: 'Find and organize creators by criteria', icon: Search, color: '#00b1a5' },
                      { title: 'Send outreach emails', desc: 'Draft and send personalized invites', icon: Mail, color: '#be43ff' },
                    ].map((card) => (
                      <button
                        key={card.title}
                        onClick={() => {
                          setInputValue(card.title.toLowerCase());
                        }}
                        className="text-left p-4 rounded-2xl border border-gray-200 bg-white hover:border-[#be43ff]/30 hover:shadow-[0_2px_12px_rgba(190,67,255,0.08)] transition-all duration-200 group"
                      >
                        <div className="flex items-center gap-2.5 mb-1.5">
                          <card.icon className="w-4 h-4" style={{ color: card.color }} />
                          <span className="text-[13px] text-[#101828] group-hover:text-[#be43ff] transition-colors" style={{ fontWeight: 600 }}>
                            {card.title}
                          </span>
                        </div>
                        <p className="text-[12px] text-[#98a2b3]">{card.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              /* ─── Messages ─── */
              <div className="flex-1 overflow-y-auto px-6 py-6">
                <div className="max-w-[720px] mx-auto space-y-6">
                  {messages.map((msg) => (
                    <div key={msg.id}>
                      {msg.role === 'user' ? (
                        <div className="flex justify-end">
                          <div className="max-w-[80%] bg-[#be43ff] text-white rounded-2xl rounded-br-md px-4 py-3 text-[14px]" style={{ lineHeight: '1.6' }}>
                            {msg.content}
                          </div>
                        </div>
                      ) : (
                        <div className="flex gap-3">
                          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#be43ff] to-[#8b1fd4] flex items-center justify-center shrink-0 mt-0.5">
                            <Zap className="w-3.5 h-3.5 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            {/* Text content */}
                            {msg.content && (
                              <div className="text-[14px] text-[#344054] mb-3" style={{ lineHeight: '1.7' }}>
                                {renderMarkdown(msg.content)}
                              </div>
                            )}

                            {/* Action blocks */}
                            {msg.blocks && msg.blocks.length > 0 && (
                              <div className="space-y-2.5">
                                {msg.blocks.map((block, idx) => (
                                  <ActionBlockCard key={idx} block={block} index={idx} />
                                ))}
                              </div>
                            )}

                            {/* Feedback row */}
                            <div className="flex items-center gap-1.5 mt-3">
                              <button className="w-7 h-7 flex items-center justify-center rounded-lg text-[#c4c9d4] hover:text-[#667085] hover:bg-gray-100 transition-colors">
                                <Copy className="w-3.5 h-3.5" />
                              </button>
                              <button className="w-7 h-7 flex items-center justify-center rounded-lg text-[#c4c9d4] hover:text-[#667085] hover:bg-gray-100 transition-colors">
                                <ThumbsUp className="w-3.5 h-3.5" />
                              </button>
                              <button className="w-7 h-7 flex items-center justify-center rounded-lg text-[#c4c9d4] hover:text-[#667085] hover:bg-gray-100 transition-colors">
                                <ThumbsDown className="w-3.5 h-3.5" />
                              </button>
                              <button className="w-7 h-7 flex items-center justify-center rounded-lg text-[#c4c9d4] hover:text-[#667085] hover:bg-gray-100 transition-colors">
                                <RotateCcw className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              </div>
            )}

            {/* Input area */}
            <div className="shrink-0 px-6 pb-5 pt-2">
              <div className="max-w-[720px] mx-auto bg-white rounded-2xl border border-gray-200 shadow-[0_2px_12px_rgba(0,0,0,0.04)] overflow-hidden">
                <div className="px-5 pt-3.5 pb-1.5">
                  <textarea
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Describe a task or tell me what to do..."
                    className="w-full text-[14px] text-[#101828] placeholder:text-[#98a2b3] outline-none resize-none bg-transparent"
                    rows={2}
                    style={{ fontWeight: 400 }}
                  />
                </div>
                <div className="flex items-center justify-between px-4 pb-2.5">
                  <div className="flex items-center gap-1">
                    <button className="w-7 h-7 flex items-center justify-center rounded-lg text-[#98a2b3] hover:text-[#667085] hover:bg-gray-50 transition-colors">
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <button className="flex items-center gap-1.5 text-[12px] text-[#667085] hover:text-[#344054] transition-colors px-2 py-1 rounded-lg hover:bg-gray-50">
                      <span style={{ fontWeight: 500 }}>Beatly Agent</span>
                      <ChevronDown className="w-3 h-3" />
                    </button>
                    <button
                      onClick={handleSend}
                      disabled={!inputValue.trim()}
                      className={cn(
                        'w-7 h-7 rounded-full flex items-center justify-center transition-all duration-200',
                        inputValue.trim()
                          ? 'bg-[#be43ff] text-white hover:bg-[#a835e0] shadow-[0_2px_8px_rgba(190,67,255,0.3)]'
                          : 'bg-gray-100 text-gray-300'
                      )}
                    >
                      <ArrowUp className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
              <p className="text-center text-[11px] text-[#c4c9d4] mt-2">
                Beatly Agent can make mistakes. Please double-check actions before confirming.
              </p>
            </div>
          </div>

          {/* ────────────── Right Panel ────────────── */}
          {rightPanelOpen && (
            <div className="w-[260px] shrink-0 border-l border-gray-100/60 bg-white overflow-y-auto">
              {/* Progress section */}
              <div className="p-4 border-b border-gray-100">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-[12px] text-[#101828] uppercase tracking-wider" style={{ fontWeight: 700 }}>Progress</h4>
                  {progressSteps.length > 0 && (
                    <span className="text-[11px] text-[#98a2b3]">
                      {progressSteps.filter(s => s.status === 'done').length} of {progressSteps.length}
                    </span>
                  )}
                </div>
                {progressSteps.length > 0 ? (
                  <div className="space-y-0">
                    {progressSteps.map((step, idx) => (
                      <div key={step.id} className="flex items-start gap-2.5 relative">
                        {/* Vertical connector */}
                        {idx < progressSteps.length - 1 && (
                          <div
                            className="absolute left-[7px] top-[18px] w-px h-[calc(100%)] bg-gray-200"
                            style={{
                              background: step.status === 'done' ? '#be43ff' : '#e5e7eb',
                            }}
                          />
                        )}
                        {/* Icon */}
                        <div className="shrink-0 mt-0.5 relative z-10">
                          {step.status === 'done' ? (
                            <CheckCircle2 className="w-[15px] h-[15px] text-[#be43ff]" />
                          ) : step.status === 'running' ? (
                            <Loader2 className="w-[15px] h-[15px] text-[#a9f600] animate-spin" />
                          ) : (
                            <Circle className="w-[15px] h-[15px] text-[#d0d5dd]" />
                          )}
                        </div>
                        {/* Text */}
                        <div className="pb-3.5">
                          <span
                            className={cn(
                              'text-[12px] block',
                              step.status === 'done' ? 'text-[#344054]' : step.status === 'running' ? 'text-[#101828]' : 'text-[#98a2b3]'
                            )}
                            style={{ fontWeight: step.status === 'running' ? 600 : 500 }}
                          >
                            {step.label}
                          </span>
                          {step.detail && (
                            <span className="text-[11px] text-[#98a2b3] mt-0.5 block">{step.detail}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[12px] text-[#98a2b3]">No active task</p>
                )}
              </div>

              {/* Agent files section */}
              <div className="p-4 border-b border-gray-100">
                <button
                  onClick={() => setFilesExpanded(!filesExpanded)}
                  className="flex items-center justify-between w-full mb-2"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 rounded bg-gradient-to-br from-[#be43ff] to-[#8b1fd4] flex items-center justify-center">
                      <Zap className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-[12px] text-[#101828]" style={{ fontWeight: 700 }}>Beatly Campaign Agent</span>
                  </div>
                  <ChevronRight className={cn('w-3.5 h-3.5 text-[#98a2b3] transition-transform', filesExpanded && 'rotate-90')} />
                </button>
                {filesExpanded && (
                  <div className="ml-1 space-y-0.5">
                    {agentFiles.map((file) => (
                      <div
                        key={file.name}
                        className="flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-[#f9fafb] transition-colors cursor-pointer"
                      >
                        {getFileIcon(file.icon)}
                        <span className="text-[11px] text-[#667085] truncate">{file.name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Context / Connectors section */}
              <div className="p-4">
                <button
                  onClick={() => setContextExpanded(!contextExpanded)}
                  className="flex items-center justify-between w-full mb-2"
                >
                  <span className="text-[12px] text-[#101828] uppercase tracking-wider" style={{ fontWeight: 700 }}>Context</span>
                  <ChevronRight className={cn('w-3.5 h-3.5 text-[#98a2b3] transition-transform', contextExpanded && 'rotate-90')} />
                </button>
                {contextExpanded && (
                  <>
                    <span className="text-[11px] text-[#98a2b3] mb-2 block">Connectors</span>
                    <div className="space-y-1">
                      {connectors.map((conn) => (
                        <div
                          key={conn.name}
                          className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-[#f9fafb] transition-colors cursor-pointer"
                        >
                          <span className="text-[14px]">{conn.icon}</span>
                          <span className="text-[12px] text-[#344054]" style={{ fontWeight: 500 }}>{conn.name}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ───────── Action Block Card ───────── */

function ActionBlockCard({ block, index }: { block: ActionBlock; index: number }) {
  if (block.type === 'status-change') {
    return (
      <div className="bg-white border border-gray-100 rounded-xl p-3.5 shadow-[0_1px_3px_rgba(0,0,0,0.03)]">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[13px] text-[#101828]" style={{ fontWeight: 600 }}>
            {index + 1}. {block.label}
          </span>
        </div>
        {block.statusFrom && block.statusTo && (
          <div className="flex items-center gap-1.5 mb-2 flex-wrap">
            <code className="px-2 py-0.5 bg-[#fff0f0] text-[#cc3340] text-[11px] rounded-md" style={{ fontFamily: 'monospace', fontWeight: 600 }}>
              {block.statusFrom}
            </code>
            <span className="text-[#98a2b3] text-[11px]">→</span>
            <code className="px-2 py-0.5 bg-[#ecfdf5] text-[#059669] text-[11px] rounded-md" style={{ fontFamily: 'monospace', fontWeight: 600 }}>
              {block.statusTo}
            </code>
          </div>
        )}
        {block.resultText && (
          <p className="text-[12px] text-[#667085]" style={{ lineHeight: '1.5' }}>{block.resultText}</p>
        )}
      </div>
    );
  }

  if (block.type === 'api-call') {
    return (
      <div className="bg-white border border-gray-100 rounded-xl p-3.5 shadow-[0_1px_3px_rgba(0,0,0,0.03)]">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[13px] text-[#101828]" style={{ fontWeight: 600 }}>
            {index + 1}. {block.label}
          </span>
        </div>
        {block.method && block.endpoint && (
          <div className="flex items-center gap-2 mb-2">
            <code
              className={cn(
                'px-2 py-0.5 text-[11px] rounded-md',
                block.method === 'GET' ? 'bg-[#eff6ff] text-[#2563eb]' :
                block.method === 'POST' ? 'bg-[#ecfdf5] text-[#059669]' :
                block.method === 'PATCH' ? 'bg-[#fef3c7] text-[#d97706]' :
                block.method === 'DELETE' ? 'bg-[#fff0f0] text-[#cc3340]' :
                'bg-gray-100 text-[#667085]'
              )}
              style={{ fontFamily: 'monospace', fontWeight: 700 }}
            >
              {block.method}
            </code>
            <code className="text-[11px] text-[#667085] truncate" style={{ fontFamily: 'monospace' }}>
              {block.endpoint}
            </code>
          </div>
        )}
        {block.resultText && (
          <p className="text-[12px] text-[#667085]" style={{ lineHeight: '1.5' }}>{block.resultText}</p>
        )}
      </div>
    );
  }

  if (block.type === 'result') {
    return (
      <div className={cn(
        'rounded-xl p-3.5 border',
        block.resultStatus === 'success' ? 'bg-[#ecfdf5] border-[#a7f3d0]' :
        block.resultStatus === 'error' ? 'bg-[#fff0f0] border-[#fecaca]' :
        'bg-[#f0f4ff] border-[#c7d2fe]'
      )}>
        <div className="flex items-center gap-2">
          {block.resultStatus === 'success' && <CheckCircle2 className="w-4 h-4 text-[#059669]" />}
          <p className="text-[12px] text-[#344054]" style={{ fontWeight: 500 }}>{block.resultText}</p>
        </div>
      </div>
    );
  }

  return null;
}

/* ───────── Markdown-ish renderer ───────── */

function renderMarkdown(text: string) {
  // Very simple bold + line rendering
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} style={{ fontWeight: 600, color: '#101828' }}>{part.slice(2, -2)}</strong>;
    }
    return <span key={i}>{part}</span>;
  });
}

/* ───────── Simulated agent responses ───────── */

function generateAgentResponse(input: string): CoworkMessage {
  const lower = input.toLowerCase();

  if (lower.includes('payout') || lower.includes('pay')) {
    return {
      id: `msg-${Date.now() + 1}`,
      role: 'agent',
      content: 'Processing payouts for all qualified collaborations in the **Spring Glow** campaign:',
      timestamp: 'Just now',
      blocks: [
        {
          type: 'api-call',
          label: 'Fetched qualified collaborations',
          method: 'GET',
          endpoint: '/bo/v2/campaign/spring-glow/collabs?status=QUALIFIED',
          resultText: 'Found 8 collaborations ready for payout processing.',
          resultStatus: 'success',
        },
        {
          type: 'status-change',
          label: 'Moved to PENDING_REVIEW',
          statusFrom: 'QUALIFIED',
          statusTo: 'PENDING_REVIEW',
          resultText: 'All 8 collaborations moved to pending review for final check.',
        },
        {
          type: 'api-call',
          label: 'Approved and requested payouts',
          method: 'POST',
          endpoint: '/bo/v2/campaign/spring-glow/payouts/batch-request',
          resultText: 'Batch payout of 142,500 SEK requested for 8 creators. Estimated processing: 3-5 business days.',
          resultStatus: 'success',
        },
        {
          type: 'result',
          resultText: 'All payouts submitted successfully! Creators will be notified via email.',
          resultStatus: 'success',
        },
      ],
    };
  }

  if (lower.includes('campaign') || lower.includes('launch')) {
    return {
      id: `msg-${Date.now() + 1}`,
      role: 'agent',
      content: 'Setting up a new campaign with the following actions:',
      timestamp: 'Just now',
      blocks: [
        {
          type: 'api-call',
          label: 'Campaign created',
          method: 'POST',
          endpoint: '/bo/v2/campaign/create',
          resultText: 'Campaign "Summer Glow 2026" created with ID: sg-2026-001',
          resultStatus: 'success',
        },
        {
          type: 'api-call',
          label: 'Briefs generated',
          method: 'POST',
          endpoint: '/bo/v2/campaign/sg-2026-001/briefs/generate',
          resultText: 'Created 3 briefs: "Brief 1 — Product Showcase", "Brief 2 — Lifestyle", "Brief 3 — Tutorial"',
          resultStatus: 'success',
        },
        {
          type: 'status-change',
          label: 'Campaign activated',
          statusFrom: 'DRAFT',
          statusTo: 'ACTIVE',
          resultText: 'Campaign is now live and accepting creator applications.',
        },
      ],
    };
  }

  if (lower.includes('creator') || lower.includes('list') || lower.includes('compile')) {
    return {
      id: `msg-${Date.now() + 1}`,
      role: 'agent',
      content: 'Compiling a targeted creator list based on your brand criteria:',
      timestamp: 'Just now',
      blocks: [
        {
          type: 'api-call',
          label: 'Discovery search executed',
          method: 'POST',
          endpoint: '/bo/v2/creators/discover',
          resultText: 'Searched 12,400 Nordic lifestyle creators. Applied filters: 15K-80K followers, >2.5% ER, beauty/skincare niche.',
          resultStatus: 'success',
        },
        {
          type: 'result',
          resultText: 'Created list "Luminara Nordic Q2" with 47 qualified creators. Top match: @amandaaxelsson (4.1% ER, 52K followers).',
          resultStatus: 'success',
        },
      ],
    };
  }

  if (lower.includes('email') || lower.includes('outreach') || lower.includes('invite')) {
    return {
      id: `msg-${Date.now() + 1}`,
      role: 'agent',
      content: 'Sending personalized outreach emails to shortlisted creators:',
      timestamp: 'Just now',
      blocks: [
        {
          type: 'api-call',
          label: 'Email templates generated',
          method: 'POST',
          endpoint: '/bo/v2/emails/generate-batch',
          resultText: '24 personalized emails drafted with dynamic merge fields (name, niche, past collab references).',
          resultStatus: 'success',
        },
        {
          type: 'api-call',
          label: 'Emails sent via Gmail',
          method: 'POST',
          endpoint: '/integrations/gmail/send-batch',
          resultText: '24 emails sent. Open tracking enabled. Estimated response rate: 62% based on historical data.',
          resultStatus: 'success',
        },
        {
          type: 'result',
          resultText: 'Outreach complete! I\'ll notify you when responses start coming in.',
          resultStatus: 'success',
        },
      ],
    };
  }

  return {
    id: `msg-${Date.now() + 1}`,
    role: 'agent',
    content: 'I\'m ready to help! I can perform actions on the Beatly platform including:',
    timestamp: 'Just now',
    blocks: [
      {
        type: 'result',
        resultText: '• Create & launch campaigns with briefs\n• Curate and invite creators\n• Process content approvals\n• Execute payout workflows\n• Send outreach emails\n• Generate analytics reports',
        resultStatus: 'info',
      },
    ],
  };
}
